<template>
	<Transition name="fade-out">
		<div v-show="active" class="side-menu h-100" :class="[`side-menu--${placement}`, `side-menu--${width}`, customClass]">
			<Transition :name="transition">
				<div v-show="active" class="side-menu__content scrollbar" :class="contentContainerClassList">
					<div v-if="placement === 'left' || placement === 'right'" class="button--no-style side-menu__close absolute">
						<button :class="`${exButtonBreakpoint}:hide`" class="button--no-style" @click="handleClose">
							<Inline svg="ex" />
						</button>
					</div>
					<slot v-if="active" />
				</div>
			</Transition>
			<Transition name="fade">
				<div v-if="active" class="side-menu__overlay" @click="handleClose" />
			</Transition>
		</div>
	</Transition>
</template>

<script lang="ts">
	import { defineComponent } from 'vue';
	import type { PropType } from 'vue';

	export type SideMenuSize = 's' | 'm' | 'l';
	export type SideMenuPlacement = 'left' | 'right' | 'top' | 'bottom';

	export default defineComponent({
		name: 'CSideMenu',
		props: {
			active: {
				type: Boolean,
				default: false
			},
			placement: {
				type: String as PropType<SideMenuPlacement>,
				default: 'left'
			},
			width: {
				type: String as PropType<SideMenuSize>,
				default: 'm'
			},
			contentContainerClassList: {
				type: String,
				default: null
			},
			customClass: {
				type: String,
				default: null
			},
			exButtonBreakpoint: {
				type: String,
				default: 'b5'
			}
		},
		computed: {
			transition() {
				if (this.placement === 'left')
					return 'slide-toggle';
				else
					return `slide-${this.placement}-toggle`;
			}
		},
		watch: {
			active: {
				handler(val) {
					if (val)
						document.documentElement.setAttribute('data-side-menu-active', '');
					else
						document.documentElement.removeAttribute('data-side-menu-active');
				},
				immediate: true
			}
		},
		mounted() {
			document.addEventListener('keyup', this.handleEscape);
		},
		beforeDestroy() {
			document.removeEventListener('keyup', this.handleEscape);
		},
		methods: {
			handleEscape(e) {
				if (e.key === 'Escape' || e.keyCode === 27) {
					this.handleClose();
				}
			},
			handleClose() {
				this.$emit('close');
			}
		}
	});
</script>

<style lang="scss" src="@cognitoforms/styles/global/_motion.scss"></style>

<style lang="scss">
.side-menu {
	position: fixed;
	top: 0;
	z-index: $z-side-menu;
	width: 100%;
	contain: strict;

	.app-container.admin--nav-collapsed & {
		transform: translateX(var(--nav-width));
	}

	&__close {
		z-index: $z2;
		max-width: 100%;
		height: 1px;
		text-align: right;

		button {
			width: 44px;
			height: 44px;
			animation: fade-in .3s .3s;
			animation-fill-mode: backwards;
		}
	}

	&__content {
		position: relative;
		z-index: $z1;
		width: calc(100% + $default-scrollbar-width);
		max-width: 100%;
		background: var(--bg-primary);
		overflow-x: hidden;

		@include modal-shadow;

		.side-menu--left &,
		.side-menu--right & {
			height: 100%;
		}
	}

	&--l &__content,
	&--l &__close {
		width: 550px;
	}

	&--m &__content,
	&--m &__close {
		width: 450px;
	}

	&--s &__content,
	&--s &__close {
		width: 350px;
	}

	&--right &__content {
		margin-left: auto;
	}

	&--right &__close {
		right: 0;
	}

	&--top &__content,
	&--bottom &__content {
		width: 100%;
		max-width: 100%;
		max-height: calc(100vh - 30px);
	}

	&--top &__content {
		margin-bottom: auto;
	}

	&--bottom &__content {
		margin-top: auto;
	}

	&__overlay {
		position: absolute;
		bottom: 0;
		left: 0;
		right: 0;
		top: 0;
		z-index: 0;
		background: var(--bg-overlay);
	}
}

html[data-side-menu-active] {
	overflow: hidden;
}
</style>